import styled from 'styled-components';

import { ANIM, LOGIN, LOGIN_MOBILE } from '../../theme/tokens';

/**
 * Change-password page styles (SPB-480): the authenticated sibling of `/reset-password`, so it
 * reuses the whole `/login` + `/register` design language, the same background, glass body card,
 * white input pill, orange CTA, footer link, all from the `LOGIN` token group. The card is
 * KEPT-DARK like its siblings (every LOGIN colour resolves to its dark literal in both themes),
 * so nothing here is theme-scoped. The node tree mirrors the
 * `.page > .main > .header/.body > .body-inner > .layout > .wrapper` structure of
 * `reset-password.styles.ts` so the markup in `index.tsx` stays a 1:1 sibling. The three layouts
 * reuse two wrappers: `.form-view` styles both the intro ("email me a code") view AND the
 * enter-code / set-new-password view, `.sent-view` styles the "password changed" confirmation.
 * Root class is `nova-change-password` (non-colliding, same reason the reset root is
 * `nova-reset-password`). The `@media` block mirrors the siblings' reflow.
 */
export const ChangePassword = styled.div`
  width: 100dvw;
  min-height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: ${LOGIN.pageBg};
  background-image: ${LOGIN.bgImage};
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: cover;

  /* Firefox */
  & {
    scrollbar-color: ${LOGIN.scrollColorThumb} ${LOGIN.scrollColorTrack};
  }

  /* Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${LOGIN.scrollTrack};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${LOGIN.scrollThumb};
    border-radius: 10px;
    border: 3px solid ${LOGIN.scrollThumbBorder};
  }

  /* The app sets \`color-scheme: dark\` globally, which would turn the white input
     field white-on-white. Force the light UA scheme on text inputs (as /login does). */
  & input:not([type='checkbox']):not([type='radio']) {
    color-scheme: light;
  }

  .page {
    width: 100%;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .main {
    width: 480px;
    padding: 48px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 64px;
  }

  .header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .logo {
    width: 200px;
    display: flex;

    svg,
    img {
      width: 100%;
      height: auto;
      cursor: pointer;
    }
  }

  /* The card hugs its content and morphs between the intro and confirmation heights (inline
     height from useAuthCardMorph, eased). Padding lives on \`.body-inner\` so the measured
     height already includes it. */
  .body {
    width: 480px;
    flex-shrink: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    overflow: hidden;
    padding: 0;
    border-radius: 24px;
    background: ${LOGIN.bodyBg};
    box-shadow: ${LOGIN.cardShadow};
    transition: height ${ANIM.DRAWER_MS}ms ${ANIM.EASE};
  }

  .body-inner {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    padding: 24px;
  }

  @media (prefers-reduced-motion: reduce) {
    .body {
      transition: none;
    }
    /* Strength meter: reveal + bar-fill motion snaps; only the colour still transitions. */
    .input .password-strength {
      transition: none;
      transform: none;

      .strength-bars .bar::before {
        transition: background-color ${ANIM.INTERACT_MS}ms ease;
      }
    }
  }

  .layout {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
  }

  /* Form view: a left-aligned, content-height stack on a 24px rhythm, heading / inputs /
     submit button, then a divider and the footer. Styles both the intro and the enter-code
     views. */
  .wrapper.form-view {
    height: auto;
    justify-content: flex-start;
    align-items: stretch;
    gap: 24px;

    .sign-in-form {
      height: auto;
      align-items: stretch;
      gap: 12px;
    }

    .heading {
      align-items: flex-start;
      margin-bottom: 12px;
    }

    .title {
      max-width: none;
      text-align: left;
    }

    .subtitle {
      text-align: left;
    }

    .cta {
      align-items: stretch;
    }

    .footer {
      justify-content: flex-start;
    }
  }

  /* Confirmation ("password changed") view: heading then the footer link back to settings.
     Deliberately understated, no illustration (start subtle). */
  .wrapper.sent-view {
    height: auto;
    justify-content: flex-start;
    align-items: stretch;
    gap: 24px;

    .heading {
      align-items: flex-start;
      gap: 12px;
    }

    .heading-copy {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      width: 100%;
    }

    .title {
      max-width: none;
      text-align: left;
    }

    .subtitle {
      text-align: left;
    }
  }

  .sign-in-form {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    gap: 48px;
  }

  .heading {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  .title {
    max-width: 420px;
    font-size: 36px;
    font-style: normal;
    font-weight: 800;
    line-height: 1.12;
    text-align: center;
    background: ${LOGIN.titleGradient};
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    /* Same descender clipping guard as /login and /register: pad the paint box so a
       last-line descender is not cropped, negative margin keeps layout unchanged. */
    padding-bottom: 4px;
    margin-bottom: -4px;
  }

  .subtitle {
    color: ${LOGIN.subtitle};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.43;
    text-align: center;
  }

  .input {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;

    input[type='email'] {
      height: 48px;
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 0 12px;
      border-radius: 999px;
      border: 1px solid ${LOGIN.inputBorder};
      background: ${LOGIN.inputBg};
      outline: 0;
      transition: border-color ${ANIM.INTERACT_MS}ms ease;

      &:focus-visible {
        border-color: ${LOGIN.inputFocusBorder};
      }

      &::placeholder {
        color: ${LOGIN.inputPlaceholder};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
      }
    }

    /* Confirmation-code field (enter-code view): the same white pill as the email field, with
       wide tracking so a 6-digit code is easy to read back against the email. It is a text input
       (numeric inputMode) so it sits OUTSIDE the \`.password-input\` reveal rule below. */
    input.code-field {
      height: 48px;
      display: flex;
      align-items: center;
      padding: 0 12px;
      border-radius: 999px;
      border: 1px solid ${LOGIN.inputBorder};
      background: ${LOGIN.inputBg};
      outline: 0;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 4px;
      transition: border-color ${ANIM.INTERACT_MS}ms ease;

      &:focus-visible {
        border-color: ${LOGIN.inputFocusBorder};
      }

      &::placeholder {
        color: ${LOGIN.inputPlaceholder};
        font-size: 14px;
        font-weight: 400;
        letter-spacing: normal;
        line-height: 20px;
      }
    }

    /* New-password row for the set-new-password view. Both password and text (eye-revealed)
       types need full metrics or the revealed field shrinks to the UA default (the
       eye-toggle-shrink lesson from /login and register). The relative wrapper lets the
       show/hide button sit inside the pill; padding-right clears it. */
    .password-input {
      position: relative;

      input[type='password'],
      input[type='text'] {
        width: 100%;
        height: 48px;
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 0 44px 0 12px;
        border-radius: 999px;
        border: 1px solid ${LOGIN.inputBorder};
        background: ${LOGIN.inputBg};
        outline: 0;
        transition: border-color ${ANIM.INTERACT_MS}ms ease;

        &:focus-visible {
          border-color: ${LOGIN.inputFocusBorder};
        }

        &::placeholder {
          color: ${LOGIN.inputPlaceholder};
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 20px;
        }
      }

      .eye-toggle {
        position: absolute;
        top: 50%;
        right: 4px;
        transform: translateY(-50%);
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 0;
        background: transparent;
        color: ${LOGIN.subtitle};
        cursor: pointer;
        transition: color ${ANIM.INTERACT_MS}ms ease;

        .bi {
          font-size: 18px;
          display: inline-flex;
        }

        &:hover {
          color: ${LOGIN.inputPlaceholder};
        }
      }
    }

    /* Resend affordance under the code field: a muted label + a link-styled button that
       re-requests a fresh code. */
    .resend {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;

      .resend-label {
        color: ${LOGIN.subtitle};
        font-size: 12px;
        font-weight: 400;
        line-height: 1.4;
      }

      .resend-link {
        padding: 0;
        border: 0;
        background: transparent;
        color: ${LOGIN.footerLink};
        font-size: 12px;
        font-weight: 500;
        line-height: 1.4;
        cursor: pointer;
        transition: filter ${ANIM.INTERACT_MS}ms ease;

        &:hover {
          filter: brightness(1.2);
        }

        &:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
      }
    }

    /* Password strength meter + caption (same behaviour as register): reserved height at all
       times, hidden until the first keystroke then fades in, bars fill left-to-right, uniform
       colour per state (too-short red -> weak orange -> fair amber -> strong green). */
    .password-strength {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-height: 30px;
      text-align: left;

      opacity: 0;
      visibility: hidden;
      transform: translateY(-4px);
      transition:
        opacity ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
        transform ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
        visibility 0s linear ${ANIM.DRAWER_MS}ms;

      &.is-visible {
        opacity: 1;
        visibility: visible;
        transform: none;
        transition:
          opacity ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
          transform ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
          visibility 0s;
      }

      .strength-bars {
        display: flex;
        gap: 6px;

        .bar {
          flex: 1;
          height: 4px;
          position: relative;
          overflow: hidden;
          border-radius: 999px;
          background: ${LOGIN.strengthTrack};

          &::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: inherit;
            transform: scaleX(0);
            transform-origin: left;
            transition:
              transform ${ANIM.INTERACT_MS}ms ease,
              background-color ${ANIM.INTERACT_MS}ms ease;
          }

          &.filled::before {
            transform: scaleX(1);
          }
        }
      }

      .strength-caption {
        font-size: 12px;
        font-weight: 500;
        line-height: 1.4;
        min-height: 17px;
        color: ${LOGIN.subtitle};
      }

      &.strength-too-short {
        .bar.filled::before {
          background: ${LOGIN.strengthTooShort};
        }
        .strength-caption {
          color: ${LOGIN.strengthTooShort};
        }
      }

      &.strength-weak {
        .bar.filled::before {
          background: ${LOGIN.strengthWeak};
        }
        .strength-caption {
          color: ${LOGIN.strengthWeak};
        }
      }

      &.strength-fair {
        .bar.filled::before {
          background: ${LOGIN.strengthFair};
        }
        .strength-caption {
          color: ${LOGIN.strengthFair};
        }
      }

      &.strength-strong {
        .bar.filled::before {
          background: ${LOGIN.strengthStrong};
        }
        .strength-caption {
          color: ${LOGIN.strengthStrong};
        }
      }
    }
  }

  /* Error notice for a failed request/confirm (no token, expired code, wrong code, or a generic
     BE failure). Same treatment as the /login expired-link notice; useAuthCardMorph re-measures. */
  .link-notice {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid ${LOGIN.errorBoxBorder};
    background: ${LOGIN.errorBoxBg};
    text-align: left;

    .notice-icon {
      flex-shrink: 0;
      font-size: 16px;
      line-height: 20px;
      color: ${LOGIN.errorBoxIcon};
      display: inline-flex;
    }

    .notice-copy {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .notice-title {
      color: ${LOGIN.errorBoxText};
      font-size: 14px;
      font-weight: 600;
      line-height: 1.35;
    }

    .notice-hint {
      color: ${LOGIN.subtitle};
      font-size: 12px;
      font-weight: 400;
      line-height: 1.4;
    }
  }

  .cta {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .button {
      width: 100%;
      height: 48px;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;
      padding: 0 16px;
      border: 0;
      border-radius: 9999px;
      background: ${LOGIN.cta};
      cursor: pointer;
      pointer-events: all;

      .text {
        color: ${LOGIN.ctaText};
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 20px;
      }

      .icon {
        color: ${LOGIN.ctaText};
        font-size: 20px;
        display: inline-flex;
      }

      &.disabled,
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }

  /* The hairline between the CTA and the footer, repointed at the card's own kept-dark
     token (the shared styled divider follows the theme and would vanish here). */
  .auth-divider {
    background: ${LOGIN.cardDivider};
  }

  .footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    .label {
      color: ${LOGIN.footerLabel};
      font-size: 16px;
      font-style: normal;
      font-weight: 300;
      line-height: 110%;
    }

    .link {
      color: ${LOGIN.footerLink};
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 110%;
      text-decoration: none;
    }
  }

  ${LOGIN_MOBILE} {
    &,
    .page,
    .main,
    .body,
    .header,
    .footer {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
    }
    & {
      min-height: 100vh !important;
      height: auto !important;
      overflow-y: auto !important;
      justify-content: flex-start !important;
    }
    .main {
      /* Clear the status bar / home indicator on the native full-bleed page. */
      padding: calc(24px + env(safe-area-inset-top)) 12px calc(24px + env(safe-area-inset-bottom)) !important;
      gap: 40px !important;
      overflow-x: hidden !important;
    }
    .body-inner {
      padding: 24px 20px !important;
    }
    .wrapper,
    .sign-in-form,
    .heading,
    .input,
    .cta {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    .heading .title {
      font-size: 28px !important;
      line-height: 1.15 !important;
      word-wrap: break-word !important;
    }
    .heading .subtitle {
      font-size: 14px !important;
      line-height: 1.4 !important;
    }
    .button {
      width: 100% !important;
      max-width: 100% !important;
    }
    .header .logo img {
      max-width: 100% !important;
      height: auto !important;
    }
    /* NB: \`.body\` is deliberately NOT reset to auto height, its inline px value drives
       the morph; a \`height: auto !important\` here would beat the inline style. */
    .main,
    .layout,
    .wrapper,
    .sign-in-form,
    .cta {
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow: visible !important;
      justify-content: flex-start !important;
    }
    .page {
      min-height: 100dvh !important;
      height: auto !important;
      overflow: visible !important;
      justify-content: center !important;
    }
    .language-switcher {
      margin: 16px auto 8px !important;
    }
    .footer {
      display: flex !important;
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
      align-items: center !important;
      gap: 8px !important;
      text-align: left !important;
    }
    .footer .label,
    .footer .link {
      white-space: nowrap !important;
    }
  }
`;
